import React, { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { DefaultStockType, ScheduleType } from 'types/schedule'
import { AccordionCheckbox, AccordionItem, AccordionWrapper } from 'components/Accordion'
import { FormLabel } from 'components/Label'
import { HorizontalLine, Spacer } from 'components/Utility'
import { Headline, Description, SubDescription } from 'components/Typography'
import { useStringChangeEvent } from 'hooks/form'
import { useStockAmountOptions } from 'hooks/stock'
import { ChoiceFormWrapper, MultiDefaultStockForm, RadioButton, SelectBox, Textarea } from 'components/Form'
import { FirebaseDocRefType } from 'firebase'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { Card, CardInner } from 'components/Card'
import { StockUnionType } from 'types/stock'

type Props = {
  addDefaultStock: (ref: FirebaseDocRefType, type: StockUnionType) => void
  addSchedule: (ref: FirebaseDocRefType, type: StockUnionType) => void
  deleteDefaultStock: (ref: FirebaseDocRefType, defaultStockIndex: number, type: StockUnionType) => void
  deleteSchedule: (index: number, type: StockUnionType) => void
  handleDefaultStocks: (
    defaultStock: DefaultStockType,
    index: number,
    scheduleRef?: FirebaseDocRefType,
    type?: StockUnionType
  ) => void
  handleScheduledGeneration: (event: ChangeEvent<HTMLInputElement>) => void
  isChecked: boolean
  isScheduledGeneration: boolean
  schedules: ScheduleType[]
  selectDaysOfWeek: (dayOfWeek: DayOfWeekType, index: number, type: StockUnionType | '') => void
  selectAllDaysOfWeek: (daysOfWeek: DayOfWeekType[], index: number, type: StockUnionType | '') => void
  setDailySchedules: Dispatch<SetStateAction<ScheduleType[]>>
  unselectAllDaysOfWeek: (index: number, type: StockUnionType | '') => void
  selectMaximumOrder: (value: string, type: StockUnionType) => void
  toggleCheckbox: () => void
  type: StockUnionType
  updateNote: (event: React.FocusEvent<HTMLTextAreaElement>, type: StockUnionType) => void
}

const ProductEditDailySchedule: FC<Props> = (props) => {
  const {
    addDefaultStock,
    addSchedule,
    deleteDefaultStock,
    deleteSchedule,
    handleDefaultStocks,
    handleScheduledGeneration,
    isChecked,
    isScheduledGeneration,
    schedules,
    selectDaysOfWeek,
    selectMaximumOrder,
    selectAllDaysOfWeek,
    setDailySchedules,
    unselectAllDaysOfWeek,
    toggleCheckbox,
    type,
    updateNote,
  } = props

  const [note, setNote] = useState<string>('')
  const [isInit, setInit] = useState<boolean>(false)

  const handleNote = useStringChangeEvent(setNote)
  const maximumOrderOptions = useStockAmountOptions()

  // schedulesの初期値を設定する（2回目以降の更新はスキップ）
  useEffect(() => {
    if (schedules.length === 0) return
    const schedule = schedules[0]

    // 商品受渡し時の注意点が設定されていれば更新
    if (schedule.note !== '' && !isInit) {
      setNote(schedule.note)
      setInit(true)
    }
  }, [schedules])

  return (
    <AccordionWrapper>
      <AccordionCheckbox id={type} label={'当日取り置き'} isChecked={isChecked} toggleCheckbox={toggleCheckbox}>
        <AccordionItem>
          <Spacer size={16} />
          <Headline headline={`当日取り置きの設定`} />
          <Spacer size={16} />
          <Description description={'当日在庫は自由に増減が可能です。'} />
          <Spacer size={28} />
          <FormLabel label={'おひとりさま購入上限数'} required={true} />
          <Spacer size={8} />
          <SubDescription description={'お客さまが、1回の注文で購入できる上限を設定してください。'} />
          <Spacer size={8} />
          <SelectBox
            options={maximumOrderOptions}
            select={(value) => selectMaximumOrder(value, type)}
            value={schedules[0] ? String(schedules[0].maximum_order) : '0'}
          />
          <Spacer size={28} />
          <FormLabel id={`${type}-choices`} label={'商品の受渡しオプション'} required={false} />
          <Spacer size={8} />
          <SubDescription
            description={
              '食パンのスライスなど、お客様が購入する際に選択できるオプションがある場合は、設定してください。\n' +
              '※項目を設定した場合、お客様はオプションの中からいずれかを選択する形になりますので、ご注意ください。'
            }
          />
          <Spacer size={8} />
          <ChoiceFormWrapper
            choices={schedules[0] ? schedules[0].choices : []}
            setSchedules={setDailySchedules}
            type={type}
          />
          <Spacer size={28} />
          <Textarea
            disabled={false}
            label={'商品受渡し時の注意点'}
            maxLength={280}
            onBlur={(event) => updateNote(event, type)}
            onChange={handleNote}
            placeholder={
              '(記入例)\n手持ち袋は有料になりますので、エコバッグをお持ち下さい。\nポイントカードをお持ちの方は受け取り時にお声がけください。'
            }
            required={false}
            rows={16}
            value={note}
          />
          <Spacer size={28} />
          <Card isOutlined={true}>
            <CardInner>
              <RadioButton
                isChecked={isScheduledGeneration}
                label={'自動配信'}
                onChange={handleScheduledGeneration}
                value={'scheduled'}
              />
            </CardInner>
            <HorizontalLine color={'grey03'} verticalMargin={16} />
            <CardInner>
              <FormLabel id={'daily-stock'} label={'受渡解禁時刻とオープン時在庫数'} required={true} />
              <Spacer size={8} />
              <SubDescription description={'商品が受け渡しできる時刻とオープン時の在庫数を設定してください。'} />
              <Spacer size={12} />
              <MultiDefaultStockForm
                addDefaultStock={addDefaultStock}
                addSchedule={addSchedule}
                deleteDefaultStock={deleteDefaultStock}
                deleteSchedule={deleteSchedule}
                handleDefaultStocks={handleDefaultStocks}
                schedules={schedules}
                selectDaysOfWeek={selectDaysOfWeek}
                selectAllDaysOfWeek={selectAllDaysOfWeek}
                unselectAllDaysOfWeek={unselectAllDaysOfWeek}
                type={'daily'}
              />
            </CardInner>
          </Card>
          <Spacer size={8} />
          <Card isOutlined={true}>
            <CardInner>
              <RadioButton
                isChecked={!isScheduledGeneration}
                label={'都度配信'}
                onChange={handleScheduledGeneration}
                value={'manual'}
              />
            </CardInner>
          </Card>
          <Spacer size={20} />
        </AccordionItem>
      </AccordionCheckbox>
    </AccordionWrapper>
  )
}

export default ProductEditDailySchedule
